import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import IPaciente from "../../../types/Paciente";
import { createTheme } from '@mui/material/styles';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PacienteService from "../../../services/PacienteService";
import { Typography, TextField, Button, Avatar, Paper, Container, Grid, MenuItem, CircularProgress, Alert, InputAdornment, IconButton, Switch, FormControlLabel, Snackbar, Tooltip } from "@mui/material";
import PersonAdd from '@mui/icons-material/PersonAdd';
import AddIcon from '@mui/icons-material/Add';
import Utilities from "../../../common/Utilities";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import * as constantes from "../../../common/Constantes";
import { geocodeByAddress } from "react-places-autocomplete";
import IAlert from "../../../types/Alert";
import ConfigUtils from "../../../utils/Config";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { usePhoneInput } from "../../../hooks/usePhoneInput";
import { sugerenciaEmail } from "../../../utils/EmailSospechoso";

const locale = constantes.locale;

const schema = yup.object({
    nombre: yup.string().required('El nombre es obligatorio'),
    apellidos: yup.string().required('Los apellidos son obligatorios'),    
    fechaNacimiento: yup.date().typeError('Debe ser una fecha válida').nullable().notRequired(),
    telefono: yup.string().required('El teléfono es obligatorio'),
    email: yup.string().email().nullable().notRequired()
});

type PacienteForm = yup.InferType<typeof schema> & {
  domicilio?: string;
  CP?: string;
  sexo?: string;
  conocido?: string;
  masConocido?: string;
  actividadFisica?: string;
  ocupacion?: string;
  especialistaSalud?: string;
  actividad?: boolean;
  DNI?: string;
  telefonoAdicional?: string;
};

const PacienteForm = () => {
    const theme = createTheme();
    const history = useNavigate();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const {control, register, handleSubmit, reset, setValue, watch, formState: { errors }} = useForm<PacienteForm>({ resolver: yupResolver(schema) });
    // Dominio mal escrito (gamil.com…): se avisa con la corrección, sin impedir guardar.
    const emailSugerido = sugerenciaEmail(watch('email'));

    let location = useLocation();
    const [paciente] = useState<IPaciente>(location.state);
    const [address, setAddress] = useState<string>(location.state ? location.state.domicilio : '');
    const [zipCode, setZipCode] = useState<string>(location.state ? location.state.CP : '');
    const [fechaNacimiento, setFechaNacimiento] = useState<Date | null>(location.state?.fechaNacimiento ? new Date(location.state.fechaNacimiento) : null);
    type AddressStatus = 'idle' | 'not_found' | 'ambiguous' | 'no_postal_code';
    const [addressStatus, setAddressStatus] = useState<AddressStatus>('idle');

    const [multiNumber, setMultiNumber] = useState(false);
    const [passport, setPassport] = useState(false);
    const [errorDNI, setErrorDNI] = useState('');
    const pais = ConfigUtils.getPais() || 'ES';

    const {
      value: telefono,
      setValue: setTelefono,
      error: errorTelefono,
      validate: validateTelefono,
      reset: resetTelefono,
      normalized: telefonoNormalizado
    } = usePhoneInput();

    const {
      value: telefonoAdicional,
      setValue: setTelefonoAdicional,
      error: errorTelefonoAdicional,
      validate: validateTelefonoAdicional,
      reset: resetTelefonoAdicional,
      normalized: telefonoNormalizadoAdicional
    } = usePhoneInput();

    useEffect(() => {
        const normalizedPaciente: PacienteForm = {
          ...paciente,
          fechaNacimiento: paciente?.fechaNacimiento
            ? new Date(paciente.fechaNacimiento)
            : null,
          email: paciente?.email ?? null,
        };
        reset(normalizedPaciente);

        if (paciente) {
            resetTelefono(paciente.telefono || '');
            resetTelefonoAdicional(paciente.telefonoAdicional || '');
        }
        setPassport(location.state && location.state.DNI ? Utilities.getSpanichIDType(location.state.DNI) === "Pasaporte" ? true : false : false);

    }, [paciente, reset]);

    const onSubmit = (data: PacienteForm) => {

        const payload: IPaciente = {
            ...paciente,
            ...data,
            fechaNacimiento: data.fechaNacimiento
            ? data.fechaNacimiento.toISOString()
            : '',
            email: data.email ?? '',
        };

        const telefonoOk = validateTelefono();
        if (!telefonoOk) return;

        payload.telefono = telefonoNormalizado;

        if (telefonoAdicional) {
          const ok = validateTelefonoAdicional();
          if (!ok) return;

          payload.telefonoAdicional = telefonoNormalizadoAdicional;
        }

        if (!Utilities.isNullEmptyUndefined(payload.DNI)) {
            if (passport) {
                //Validamos el pasaporte
                if (!Utilities.validatePassport(payload.DNI)) {
                    setErrorDNI('El pasaporte no tiene un formato válido');
                    return;
                }
            } else if (pais !== 'ES') {
                //Validamos el documento de identidad genérico (fuera de España)
                if (!Utilities.validateTaxId(payload.DNI, pais)) {
                    setErrorDNI(`${Utilities.getNifLabel(pais)} no tiene un formato válido`);
                    return;
                }
            } else {
                const idType = Utilities.getSpanichIDType(payload.DNI);
                // Tipo no reconocido
                if (!idType) {
                    setErrorDNI('El documento no tiene un formato válido');
                    return;
                }
                if (!Utilities.validateSpanishID(payload.DNI)) {
                    setErrorDNI(
                        idType === "CIF"
                            ? 'El NIF de empresa no tiene un formato válido'
                            : 'El DNI o NIE no tiene un formato válido'
                    );
                    return;
                }
            }
        }
        setLoading(true);
        paciente ? updatePaciente(payload) : savePaciente(payload);
    };

    const clear = () => {
        reset();
        history(-1);
    };

    const savePaciente = (data: IPaciente) => {
        data.userId = ConfigUtils.getUserId();
        PacienteService.create(data)
        .then((response: any) => {
            setLoading(false);
            history(`${constantes.R_PACIENTE}?${constantes.QP_ID}=${response.data._id}`, { state: { open: true, severity: 'success', msg: 'Paciente guardado correctamente' } });
        })
        .catch ((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'PacienteForm.Component.savePaciente', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                setLoading(false);
            }
        });
    };

    const updatePaciente = (data: IPaciente) => {
        PacienteService.update(paciente._id, data)
        .then((_: any) => {
            setLoading(false);
            history(`${constantes.R_PACIENTE}?${constantes.QP_ID}=${paciente._id}`, { state: { open: true, severity: 'success', msg: 'Paciente actualizado correctamente' } });
        })
        .catch ((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'PacienteForm.Component.updatePaciente', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                setLoading(false);
            }
        });
    };

    const handleChange = (e: any) => {
        if (e.target.name === 'actividad') {
            setValue(e.target.name, e.target.checked);
        } else if (e.target.name === 'domicilio') {
            setValue(e.target.name, e.target.value);
            setAddress(e.target.value);
            // El CP quedaba obsoleto: pertenecía a la dirección anterior y no se
            // podía corregir porque el campo es de solo lectura.
            setValue('CP', '');
            setZipCode('');
            setAddressStatus('idle');
        } else {
            setValue(e.target.name, e.target.value);
        }
    };

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const getZipCode = () => {
        if (address === '') return;
        geocodeByAddress(address).then((result) => {
            if (result.length === 0) {
                setAddressStatus('not_found');
                return;
            }
            if (result.length > 1) {
                setAddressStatus('ambiguous');
                return;
            }

            setValue('domicilio', result[0].formatted_address);
            setAddress(result[0].formatted_address);

            const data = result[0].address_components.find((component) => component.types.includes(constantes.googleAPI_postalCode));
            const cp = data?.long_name || '';
            setValue('CP', cp);
            setZipCode(cp);

            // Muchas direcciones (frecuente fuera de España) no tienen código postal indexado
            // en Google: la dirección es válida igualmente, no es un error.
            setAddressStatus(cp ? 'idle' : 'no_postal_code');
        })
        .catch((e: any) => {
            setAddressStatus('not_found');
        });
    }

    const addressHelperText: Record<AddressStatus, string> = {
        idle: '',
        not_found: 'No se ha encontrado la dirección',
        ambiguous: 'Se han encontrado varias direcciones, sé más específico',
        no_postal_code: 'No se ha encontrado el código postal para esta dirección',
    };

    try {
        return (
            <Container maxWidth="lg">
                <Paper elevation={3} sx={{ marginTop: theme.spacing(3), marginBottom: theme.spacing(), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div>
                        <Typography variant="h4" sx={{ display: 'inline-flex', margin: theme.spacing(1, 0, 1) }}>
                            <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}> <PersonAdd /></Avatar><div style={{ margin: '10px auto' }}>{paciente ? 'Editar paciente' : 'Nuevo paciente'}</div>
                        </Typography>
                        <FormControlLabel control={<Switch defaultChecked={paciente?.actividad} {...register("actividad")} onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} />} label={`Clases en ${ConfigUtils.getClinicName()}?`} labelPlacement="start" />
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Grid container>
                        <Grid item md={5} style={{ margin: 'auto' }}>
                            <TextField defaultValue={paciente?.nombre} {...register("nombre")} onChange={handleChange} variant="outlined" label="Nombre" margin="dense" fullWidth required autoFocus helperText={errors.nombre?.message} error={!!errors.nombre?.message} />
                            <TextField defaultValue={paciente?.apellidos} {...register("apellidos")} onChange={handleChange} variant="outlined" label="Apellidos" margin="dense" fullWidth required helperText={errors.apellidos?.message} error={!!errors.apellidos?.message} />
                            <TextField defaultValue={paciente?.DNI} {...register("DNI")} onChange={() => {handleChange; setErrorDNI('')}} variant="outlined" label={passport ? "Pasaporte" : Utilities.getNifLabel(pais)} margin="dense" fullWidth helperText={errorDNI} error={!!errorDNI} InputProps={{ endAdornment: (<InputAdornment position="end"><Switch checked={passport} onChange={() => {setPassport(!passport); setErrorDNI('')}} inputProps={{ 'aria-label': 'controlled' }} /></InputAdornment>) }} />
                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                              <DatePicker
                                label="Fecha de nacimiento"
                                toolbarFormat="dd-mm-yyyy"
                                value={fechaNacimiento}
                                onChange={(date: any) => {
                                  const newDate = date || null;
                                  setFechaNacimiento(newDate);
                                  setValue('fechaNacimiento', newDate, { shouldValidate: true });
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    fullWidth
                                    margin="dense"
                                    variant="outlined"
                                    error={!!errors.fechaNacimiento?.message}
                                    helperText={errors.fechaNacimiento?.message}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item md={5} style={{ margin: 'auto' }}>
                            <Controller
                              name="telefono"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  value={telefono}
                                  onChange={(e) => {
                                    setTelefono(e.target.value);
                                    field.onChange(e.target.value);
                                  }}
                                  label="Teléfono"
                                  fullWidth
                                  required
                                  error={!!errorTelefono}
                                  helperText={errorTelefono}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton onClick={() => setMultiNumber(true)}>
                                          <AddIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    )
                                  }}
                                />
                              )}
                            />
                            {(multiNumber || paciente?.telefonoAdicional) && (
                              <Controller
                                name="telefonoAdicional"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    value={telefonoAdicional}
                                    onChange={(e) => {
                                      setTelefonoAdicional(e.target.value);
                                      field.onChange(e.target.value);
                                    }}
                                    label="Otro teléfono"
                                    fullWidth
                                    error={!!errorTelefonoAdicional}
                                    helperText={errorTelefonoAdicional}
                                  />
                                )}
                              />
                            )}
                            <TextField
                              defaultValue={paciente?.email}
                              {...register("email")}
                              onChange={handleChange}
                              variant="outlined"
                              label="Email"
                              margin="dense"
                              fullWidth
                              error={!!errors.email?.message}
                              helperText={errors.email?.message}
                              // La corrección va dentro del campo para que su altura no cambie y no desplace el resto
                              InputProps={{
                                endAdornment: emailSugerido && !errors.email ? (
                                  <InputAdornment position="end">
                                    <Tooltip title={`¿Querías decir ${emailSugerido}?`} enterTouchDelay={0}>
                                      <Button
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        onClick={() => setValue('email', emailSugerido, { shouldValidate: true })}
                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                      >
                                        {`¿@${emailSugerido.split('@').pop()}?`}
                                      </Button>
                                    </Tooltip>
                                  </InputAdornment>
                                ) : undefined
                              }}
                            />
                            <TextField value={address} {...register("domicilio")} onChange={handleChange} variant="outlined" label="Domicilio" margin="dense" fullWidth />
                            <TextField value={zipCode} {...register("CP")} onFocus={getZipCode} variant="outlined" label="Código postal" margin="dense" fullWidth InputProps={{ readOnly: true }} helperText={addressHelperText[addressStatus]} error={addressStatus === 'not_found' || addressStatus === 'ambiguous'} />
                        </Grid>
                        
                        <Grid item md={3} xs={12} style={{ margin: 'auto' }}>
                            <TextField select defaultValue={paciente?.sexo ? paciente.sexo : ''} {...register("sexo")} variant="outlined" label="Sexo" margin="dense" fullWidth >
                                {constantes.sexos.map((option, index) => (
                                    <MenuItem key={index} value={index}>{option}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item md={3} xs={12} style={{ margin: 'auto' }}>
                            <TextField select defaultValue={paciente?.conocido ? paciente.conocido : ''} {...register("conocido")} variant="outlined" label="¿Cómo nos has conocido?" margin="dense" fullWidth >
                                {constantes.formaConocer.map((option, index) => (
                                    <MenuItem key={index} value={index}>{option}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item md={3} xs={12} style={{ margin: 'auto' }}>
                            <TextField defaultValue={paciente?.masConocido} {...register("masConocido")} variant="outlined" label="Más..." margin="dense" fullWidth />
                        </Grid>
                        <Grid item md={12} xs={12} style={{ margin: 'auto' }}>
                            <TextField multiline rows={3} defaultValue={paciente?.actividadFisica} {...register("actividadFisica")} onChange={handleChange} variant="outlined" label="Deportes, aficiciones, actividad física habital" margin="dense" fullWidth />
                            <TextField multiline rows={3} defaultValue={paciente?.ocupacion} {...register("ocupacion")} onChange={handleChange} variant="outlined" label="Ocupación laboral y condiciones del lugar laboral" margin="dense" fullWidth />
                            <TextField multiline rows={3} defaultValue={paciente?.especialistaSalud} {...register("especialistaSalud")} onChange={handleChange} variant="outlined" label="¿Has acudido antes a algún profesional de la salud? ¿Por qué? ¿Cuándo?" margin="dense" fullWidth />
                        </Grid>

                        <Grid style={{ margin: 'auto' }}>
                            { loading ? <CircularProgress /> : 
                            <>
                                <Button sx={{ margin: theme.spacing(2, 3, 1, 0) }} variant="contained" color="primary" type="submit" >Guardar</Button>
                                <Button sx={{ margin: theme.spacing(2, 3, 1, 0) }} variant="contained" color="secondary" onClick={clear}>Cancelar</Button>
                            </>
                            }
                        </Grid>

                    </Grid>
                    </form>
                    
                </Paper>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </Container>
        );
    } catch (e: any) {
        const error: ILog = { method: 'PacienteForm.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
    
};

export default PacienteForm;